import classNames from 'classnames';
import type { ScaleBand, ScaleOrdinal } from 'd3';
import { extent, scaleOrdinal } from 'd3';
import type { CSSProperties, JSX, ReactNode } from 'react';

import type { CommonPlotProps } from '../../types';
import {
  findNearestBandIndex,
  getDimmedPlotColor,
  useChartTheme,
} from '../../utilities';
import { createCombinedLegendFormatter } from '../../utilities/create-combined-legend-formatter';
import type { BarData } from '../bar/bar-chart';
import { Legend } from '../legend/legend';
import styles from './grouped-bar-chart.module.css';

export const Dimensions = {
  BarEdgePadding: 8,
  HorizontalBandPadding: 0.3,
  HorizontalBarBandPaddingInner: 0.2,
  MinBarHeight: 6,
  MinLabelWidth: 80,
  MaxLabelWidth: 120,
  MaxLabelWidthRatio: 0.1,
  LegendHeight: 42,
  /** `.legendContainer`'s margin-top (grouped-bar-chart.module.css). */
  LegendTopMargin: 8,
  VerticalAxisLabelHeightMin: 64,
  VerticalAxisLabelHeightMax: 148,
  VerticalCategoryLabelRotation: -35,
  VerticalCategoryTruncateCap: 240,
  VerticalCategoryTruncateFloorPx: 108,
  VerticalValueLabelTruncateFloorPx: 112,
  VerticalValueLabelTruncateCap: 220,
  BarLabelPadding: 4,
  HorizontalMaxBarLabelWidthRatio: 0.9,
  HorizontalMinBarLabelSize: 30,
  VerticalBarLabelTopReserve: 20,
  VerticalBandPaddingInner: 0.045,
  VerticalBandPaddingOuter: 0.008,
  VerticalHorizontalCategoryStripPx: 24,
  VerticalCategoryBandFitRatio: 0.88,
  VerticalApproxLineHeight: 13,
  VerticalBarLabelRowHeight: 14,
  HorizontalChartStackGap: 8,
  HorizontalChartNewStackGap: 20,
  VerticalChartStackGap: 4,
  VerticalMinPlotHeightFloor: 56,
  VerticalMinPlotHeightCap: 100,
} as const;

type GroupedBarOrientation = 'horizontal' | 'vertical';

export type GroupedBarData<TData extends object = object> = {
  group: string;
  values: BarData<TData>[];
};

export type GroupedBarChartProps<TData extends object = object> = {
  data: GroupedBarData<TData>[];
  colorMap?: Record<string, string>;
  colorPalette?: string[];
  tooltipContent?: (group: string, bar: BarData<TData>) => JSX.Element;
  hideEmptyBars?: boolean;
  hideLegend?: boolean;
  showBarLabels?: boolean;
  formatLegendLabel?: (label: string) => string;
  maxLegendLabelWidth?: number;
  orientation?: GroupedBarOrientation;
} & CommonPlotProps;

export type DisplayBar<TData extends object> = BarData<TData> & {
  _colorKey: string;
};

export type DisplayGroup<TData extends object> = {
  group: string;
  values: DisplayBar<TData>[];
};

export const processGroupedBarData = <TData extends object>(
  data: GroupedBarData<TData>[],
  hideEmptyBars: boolean
): DisplayGroup<TData>[] =>
  data.map((group) => {
    const filteredValues = hideEmptyBars
      ? group.values.filter((bar) => bar.value !== 0)
      : group.values;

    // Suffix duplicate labels with "(n)" so each DisplayBar has a unique label,
    // which is what we use as the React key in HoverableBar.
    const labelCounts = new Map<string, number>();
    const dedupedValues: DisplayBar<TData>[] = filteredValues.map((bar) => {
      const count = labelCounts.get(bar.label) ?? 0;
      labelCounts.set(bar.label, count + 1);

      const displayLabel =
        count === 0 ? bar.label : `${bar.label} (${count + 1})`;
      const colorKey =
        bar.id !== null && bar.id !== undefined
          ? `${bar.id}_${bar.label}`
          : bar.label;

      return {
        ...bar,
        label: displayLabel,
        _colorKey: colorKey,
      };
    });

    return {
      group: group.group,
      values: dedupedValues,
    };
  });

export const createGroupedBarColorScale = <TData extends object>(
  displayData: DisplayGroup<TData>[],
  colorMap: Record<string, string> | undefined,
  colorPalette: string[]
): ScaleOrdinal<string, string> => {
  const colorKeys = Array.from(
    new Set(displayData.flatMap((d) => d.values.map((v) => v._colorKey)))
  );

  return (
    scaleOrdinal<string>()
      .domain(colorKeys)
      // @ts-ignore noUncheckedIndexedAccess
      .range(colorKeys.map((key, idx) => colorMap?.[key] || colorPalette[idx]))
  );
};

export const collectBarLabels = <TData extends object>(
  displayData: DisplayGroup<TData>[]
): string[] =>
  Array.from(new Set(displayData.flatMap((d) => d.values.map((v) => v.label))));

/**
 * Largest bar count across any group. Drives min plot size + legend visibility,
 * so we use the max so partially-filtered groups don't shrink the chart.
 */
export const getMaxBarCount = <TData extends object>(
  displayData: DisplayGroup<TData>[]
): number => displayData.reduce((acc, d) => Math.max(acc, d.values.length), 0);

export const calculateGroupLayout = (
  barCount: number,
  barScale: ScaleBand<string>,
  groupBandwidth: number
): { groupSpan: number; primaryOffset: number } => {
  const totalBarThickness = barCount * barScale.bandwidth();
  const totalSpacing =
    (barCount - 1) * (barScale.step() - barScale.bandwidth());
  const groupSpan = totalBarThickness + totalSpacing;
  const primaryOffset = (groupBandwidth - groupSpan) / 2;
  return { groupSpan, primaryOffset };
};

type ZeroedExtent = { min: number; max: number };

export const getZeroedExtent = <TData extends object>(
  displayData: DisplayGroup<TData>[]
): ZeroedExtent | null => {
  const allValues = displayData
    .flatMap((d) => d.values.map((v) => v.value))
    .filter((v): v is number => v !== null);
  const [minValue, maxValue] = extent(allValues);
  if (minValue === undefined || maxValue === undefined) return null;
  return {
    min: maxValue > 0 ? Math.min(minValue, 0) : minValue,
    max: maxValue < 0 ? 0 : maxValue,
  };
};

type DimmableBarProps = {
  fill: string;
  rect: { x: number; y: number; width: number; height: number };
  dimmed: boolean;
  hovered: boolean;
  children?: ReactNode;
};

/** One bar + its label; dim/hover state comes from the renderer's hit test. */
export const DimmableBar = ({
  fill,
  rect,
  dimmed,
  hovered,
  children,
}: DimmableBarProps) => {
  const { theme } = useChartTheme();
  return (
    <g
      style={
        {
          '--replots-dim-color': getDimmedPlotColor(fill, theme),
        } as CSSProperties
      }
      className={classNames(styles.barGroup, {
        // @ts-ignore noUncheckedIndexedAccess
        [styles.dimmed]: dimmed,
        // @ts-ignore noUncheckedIndexedAccess
        [styles.hovered]: hovered,
      })}
    >
      <rect
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        rx={0}
        fill={fill}
        className={classNames(styles.bar, 'bar')}
        data-testid="grouped-bar-chart-bar"
      />
      {children}
    </g>
  );
};

export type GroupedBarHit = { groupIndex: number; barIndex: number };

/**
 * The (group, bar) under a pointer: the group must contain it (group gaps
 * are dead), bars tile within the group. Null-valued bars return null.
 */
export const findGroupedBarHit = <TData extends object>(
  displayData: DisplayGroup<TData>[],
  groupStarts: readonly (number | undefined)[],
  groupThickness: number,
  barStep: number,
  barThickness: number,
  barInset: (group: DisplayGroup<TData>) => number,
  coord: number
): GroupedBarHit | null => {
  const groupIndex = findNearestBandIndex(
    groupStarts,
    groupThickness,
    coord,
    0
  );
  if (groupIndex === null) return null;
  const group = displayData[groupIndex];
  const groupStart = groupStarts[groupIndex];
  if (group === undefined || groupStart === undefined) return null;
  const barStarts = group.values.map((_, index) => index * barStep);
  const barSlop = Math.max(0, (barStep - barThickness) / 2);
  const barIndex = findNearestBandIndex(
    barStarts,
    barThickness,
    coord - groupStart - barInset(group),
    barSlop
  );
  if (barIndex === null) return null;
  const bar = group.values[barIndex];
  if (bar === undefined || bar.value === null) return null;
  return { groupIndex, barIndex };
};

type GroupedBarLegendProps<TData extends object> = {
  barLabels: string[];
  displayData: DisplayGroup<TData>[];
  colorScale: ScaleOrdinal<string, string>;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  marginLeft?: number;
  belowSvg?: boolean;
};

export const GroupedBarLegend = <TData extends object>({
  barLabels,
  displayData,
  colorScale,
  formatLegendLabel,
  maxLegendLabelWidth,
  marginLeft,
  belowSvg = false,
}: GroupedBarLegendProps<TData>) => {
  const combinedLegendFormatter = createCombinedLegendFormatter(
    formatLegendLabel,
    maxLegendLabelWidth
  );

  const colorByLabel = new Map<string, string>();
  for (const group of displayData) {
    for (const v of group.values) {
      if (!colorByLabel.has(v.label)) {
        colorByLabel.set(v.label, colorScale(v._colorKey));
      }
    }
  }

  const items = barLabels.map((label) => ({
    label,
    color: colorByLabel.get(label) ?? '#000000',
  }));

  return (
    <div
      className={classNames(
        styles.legendContainer,
        belowSvg && styles.legendBelowVerticalSvg
      )}
      style={{
        maxHeight: Dimensions.LegendHeight,
        ...(marginLeft !== undefined && { marginLeft: `${marginLeft}px` }),
      }}
    >
      <Legend items={items} formatLabel={combinedLegendFormatter} />
    </div>
  );
};
